(() => {
  'use strict';

  const SELECTORS = {
    imageId: '[data-image-id]',
    productImages: 'product-images',
    sectionType: '[data-section-type]',
    thumbItem: '[data-thumb-item]',
    thumbLink: '[data-thumb-link]',
    thumbsSlider: '[data-thumbs-slider]',
    thumbsPrev: '[data-thumbs-prev]',
    thumbsNext: '[data-thumbs-next]',
    thumbsBullet: '[data-thumbs-bullet]'
  };

  const ATTRS = {
    activeMedia: 'data-active-media',
    mediaId: 'data-media-id'
  };

  const CLASSES = {
    isActive: 'is-active'
  };

  if (customElements.get('product-thumbs')) return;

  customElements.define(
    'product-thumbs',
    class ProductThumbs extends HTMLElement {
      constructor() {
        super();
        this.container = this.closest(SELECTORS.sectionType);
        this.productImages = this.container?.querySelectorAll(SELECTORS.imageId) || [];
        this.productImagesContainer = this.container?.querySelector(SELECTORS.productImages);
        this.productThumbs = this.querySelectorAll(SELECTORS.thumbItem);
        this.thumbSlider = this.querySelector(SELECTORS.thumbsSlider);
        this.thumbLinks = this.querySelectorAll(SELECTORS.thumbLink);
        this.prevButton = this.querySelector(SELECTORS.thumbsPrev);
        this.nextButton = this.querySelector(SELECTORS.thumbsNext);
        this.bullets = this.querySelectorAll(SELECTORS.thumbsBullet);
        this.swiper = null;
      }

      connectedCallback() {
        this.handleEvents();
        this.preloadImagesOnHover();
        this.activeMediaObserver();
        this.initSwiper();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      whenSwiperReady(callback) {
        if (typeof Swiper !== 'undefined') {
          callback();
          return;
        }
        window.addEventListener('load', callback, { once: true });
      }

      initSwiper() {
        if (!this.thumbSlider || this.productThumbs.length === 0) return;

        this.whenSwiperReady(() => {
          if (typeof Swiper === 'undefined' || this.swiper) return;

          this.swiper = new Swiper(this.thumbSlider, {
            slidesPerView: '2',
            spaceBetween: 12,
            watchOverflow: true,
            navigation: {
              prevEl: this.prevButton,
              nextEl: this.nextButton
            },
            breakpoints: {
              750: {
                spaceBetween: 16,
                slidesPerView: 3,
              }
            },
            on: {
              init: () => {
                this.syncSwiperToActiveThumb();
              }
            }
          });
        });
      }

      activeMediaObserver() {
        if (!this.productImagesContainer) return;

        this.observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === ATTRS.activeMedia) {
              this.setActiveThumb();
            }
          }
        });

        this.observer.observe(this.productImagesContainer, {
          attributes: true,
          childList: false,
          subtree: false
        });
      }

      selectMedia(mediaId) {
        if (!mediaId) return;

        this.dispatchEvent(
          new CustomEvent('theme:media:select', {
            bubbles: true,
            detail: { id: mediaId }
          })
        );
      }

      handleEvents() {
        this.thumbLinks.forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            const thumbItem = link.closest(SELECTORS.thumbItem);
            const mediaId = link.getAttribute(ATTRS.mediaId);

            if (!thumbItem?.classList.contains(CLASSES.isActive)) {
              this.selectMedia(mediaId);
            }
          });

          link.addEventListener('keyup', (event) => {
            if (event.code !== 'Enter') return;

            const mediaId = link.getAttribute(ATTRS.mediaId);
            const mediaEl = this.productImagesContainer?.querySelector(`[${ATTRS.mediaId}="${mediaId}"]`);
            const focusable = mediaEl?.querySelectorAll(
              'model-viewer, video, iframe, button, [href], input, [tabindex]:not([tabindex="-1"])'
            )[0];

            if (focusable) {
              focusable.dispatchEvent(new Event('focus'));
              focusable.dispatchEvent(new Event('select'));
            }
          });
        });

        this.bullets.forEach((bullet) => {
          bullet.addEventListener('click', () => {
            const mediaId = bullet.getAttribute(ATTRS.mediaId);
            this.selectMedia(mediaId);
          });
        });
      }

      preloadImagesOnHover() {
        this.thumbLinks.forEach((link) => {
          link.addEventListener('mouseover', () => {
            const mediaId = link.getAttribute(ATTRS.mediaId);
            this.productImagesContainer
              ?.querySelector(`[${ATTRS.mediaId}="${mediaId}"] img`)
              ?.setAttribute('loading', 'eager');
          });
        });
      }

      setActiveThumb() {
        if (!this.productImagesContainer) return;

        const activeMediaId = this.productImagesContainer.getAttribute(ATTRS.activeMedia);
        const activeLink = this.querySelector(`${SELECTORS.thumbLink}[${ATTRS.mediaId}="${activeMediaId}"]`);

        this.querySelector(`${SELECTORS.thumbItem}.${CLASSES.isActive}`)?.classList.remove(CLASSES.isActive);

        if (activeLink?.parentElement) {
          activeLink.parentElement.classList.add(CLASSES.isActive);
        }

        this.thumbLinks.forEach((link) => {
          link.setAttribute('aria-current', link === activeLink ? 'true' : 'false');
        });

        this.bullets.forEach((bullet) => {
          const isActive = bullet.getAttribute(ATTRS.mediaId) === activeMediaId;
          bullet.classList.toggle(CLASSES.isActive, isActive);
          bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        requestAnimationFrame(() => {
          this.syncSwiperToActiveThumb();
        });
      }

      syncSwiperToActiveThumb() {
        if (!this.swiper) {
          this.scrollToThumb();
          return;
        }

        const activeThumb = this.querySelector(`${SELECTORS.thumbItem}.${CLASSES.isActive}`);
        if (!activeThumb) return;

        const index = Array.from(this.productThumbs).indexOf(activeThumb);
        if (index < 0) return;

        this.swiper.slideTo(index);
      }

      scrollToThumb() {
        const slider = this.thumbSlider;
        if (!slider) return;

        const activeThumb = slider.querySelector(`.${CLASSES.isActive}`);
        if (!activeThumb) return;

        const sliderLeft = slider.scrollLeft;
        const sliderWidth = slider.offsetWidth;
        const thumbLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;

        if (thumbLeft < sliderLeft || thumbLeft + thumbWidth > sliderLeft + sliderWidth) {
          slider.scrollTo({
            left: thumbLeft - (sliderWidth - thumbWidth) / 2,
            behavior: 'smooth'
          });
        }
      }
    }
  );
})();
